import { has } from 'lodash/fp'
import React, { useState, useMemo } from 'react'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import { connect, useDispatch } from 'react-redux'

import MESSAGES from 'core/data-model/constants/messages'
import { getAppConfig } from 'deploy-env/app-config'
import { getCurrentApp } from 'e-submission/apps/selectors'
import { getQuickQuoteAgentPartyCode } from 'e-submission/domain/data-model/form/selectors'
import { getAdvisor, getAdvisors } from 'e-submission/domain/service'
import { getIdentityUser } from 'identity/selectors'
import { setAppValue } from 'e-submission/apps/actions'

export const Component = AsyncTypeahead

function MyComponent({
  agentRecommender,
  user,
  partyCode,
  validate,
  onOk,
  // defaults (instead of defaultProps)
  minLength = 3,
  searchText = MESSAGES.SEARCHING,
  promptText = MESSAGES.TYPE_TO_SEARCH,
  emptyLabel = MESSAGES.DATA_NOT_FOUND,
  useCache = false,
  dropup = true,
  inputProps = { type: 'tel' },
  ...props
}) {
  const dispatch = useDispatch()
  const [options, setOptions] = useState([])
  const [errors, setErrors] = useState([])
  const [isFocus, setFocus] = useState(false)
  const [value, setValue] = useState(agentRecommender)

  const onChange = async ([recommender]) => {
    try {
      const resetValue = () => setValue(undefined)
      if (!has('party', recommender)) {
        return resetValue()
      }

      const fullRecommender = await getAdvisor(user, recommender.advisorId, recommender.type.code)
      const _val = {
        firstName: fullRecommender.party.firstName,
        lastName: fullRecommender.party.lastName,
        advisorCode: fullRecommender.advisorCode,
        advisorId: fullRecommender.advisorId,
        status: fullRecommender.status,
        registrations: fullRecommender.party.registrations,
      }
      dispatch(setAppValue(`agent.recommender`, _val))
      return setValue(_val)
    } catch (err) {
      return setValue()
    }
  }

  const onSearch = async (query) => {
    try {
      const options = await getAdvisors(user, query, partyCode, 'agent')
      setOptions(options)
    } catch (error) {
      console.log(error)
    }
  }

  const onFocus = () => {
    setFocus(true)
    validate && validate(false)
  }

  const onBlur = () => {
    setFocus(false)
    validate && validate(true)
  }

  const derivedProps = useMemo(() => {
    const minLen = getAppConfig().SEARCH_AGENT_DIGIT_LENGTH || minLength
    const selected = [value && { ...value, isFocus }]
    const labelKey = ({
      advisorCode = '',
      party = {},
      firstName = party.firstName,
      lastName = party.lastName,
      isFocus,
    }) => (isFocus ? advisorCode.split('-')[2] : `${advisorCode.split('-')[2]} - ${firstName} ${lastName}`)

    return { minLength: minLen, selected, labelKey }
  }, [value, isFocus, minLength])

  return (
    <Component
      {...{
        ...props,
        options,
        errors,
        setErrors,
        isFocus,
        value,
        onChange,
        onSearch,
        onFocus,
        onBlur,
        searchText,
        promptText,
        emptyLabel,
        useCache,
        dropup,
        inputProps,
        ...derivedProps,
      }}
    />
  )
}

export default (hoc) =>
  connect((state) => {
    const app = getCurrentApp(state)
    const user = getIdentityUser(state)
    const partyCode = getQuickQuoteAgentPartyCode(app)
    const agentRecommender = app?.agent?.recommender || null
    return { user, partyCode, agentRecommender }
  })(MyComponent)
